// src/components/admin/DriverManagement.jsx
import React, { useState, useEffect } from 'react';
import { getAllDrivers, addDriver, updateDriver, deleteDriver } from '../../services/driverService';
import { Trash2, Plus, Edit2, X } from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

const DriverManagement = ({ onBack }) => {
  const [drivers, setDrivers] = useState([]);
  const [pickupSchools, setPickupSchools] = useState([]); // unique schools from active pickup students
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    code: '',
    status: 'active',
    maxCapacity: 15,
    address: '',
    driveTimes: {}
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch all drivers on mount
  useEffect(() => {
    loadDrivers();
    loadPickupSchools();
  }, []);

  const loadPickupSchools = async () => {
    try {
      const snap = await getDocs(collection(db, 'students'));
      const schools = new Map();
      snap.docs.forEach(d => {
        const s = d.data();
        if (s.needsPickup && !s.archived && s.schoolName) {
          schools.set(s.schoolName, s.schoolAddress || '');
        }
      });
      setPickupSchools(Array.from(schools.entries()).map(([name, address]) => ({ name, address })).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      console.error('Error loading schools:', err);
    }
  };

  const loadDrivers = async () => {
    try {
      setLoading(true);
      const data = await getAllDrivers();
      setDrivers(data);
      setError('');
    } catch (err) {
      setError('Failed to load drivers');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'maxCapacity' ? parseInt(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.name.trim() || !formData.phone.trim() || !formData.code.trim()) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.code.length !== 4) {
      setError('Access code must be 4 digits');
      return;
    }

    try {
      if (editingDriver) {
        // Update existing driver
        await updateDriver(editingDriver.id, formData);
        setSuccess('Driver updated successfully');
      } else {
        // Add new driver
        await addDriver(formData);
        setSuccess('Driver added successfully');
      }

      // Reset form and reload
      setFormData({ name: '', phone: '', code: '', status: 'active', maxCapacity: 15 });
      setEditingDriver(null);
      setShowForm(false);
      await loadDrivers();
    } catch (err) {
      setError(err.message || 'Failed to save driver');
      console.error(err);
    }
  };

  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setFormData({
      name: driver.name,
      phone: driver.phone,
      code: driver.code,
      status: driver.status,
      maxCapacity: driver.maxCapacity || 15,
      address: driver.address || '',
      homeToStemtree: driver.homeToStemtree || '',
      driveTimes: driver.driveTimes || {}
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (driverId) => {
    if (window.confirm('Are you sure you want to delete this driver?')) {
      try {
        await deleteDriver(driverId);
        setSuccess('Driver deleted successfully');
        await loadDrivers();
      } catch (err) {
        setError('Failed to delete driver');
        console.error(err);
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingDriver(null);
    setFormData({ name: '', phone: '', code: '', status: 'active', maxCapacity: 15, address: '', homeToStemtree: '', driveTimes: {} });
    setError('');
    setSuccess('');
  };

  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '2rem'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem'
    },
    title: {
      fontSize: '2rem',
      fontWeight: 'bold',
      color: '#1f2937'
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1.5rem',
      backgroundColor: '#00A3E0',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontWeight: '600',
      fontSize: '0.875rem'
    },
    buttonSecondary: {
      backgroundColor: '#6b7280'
    },
    buttonDanger: {
      backgroundColor: '#ef4444'
    },
    form: {
      backgroundColor: '#f8fafc',
      padding: '2rem',
      borderRadius: '12px',
      marginBottom: '2rem',
      border: '1px solid #e5e7eb'
    },
    formGroup: {
      marginBottom: '1.5rem',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem'
    },
    formGroupFull: {
      gridColumn: '1 / -1'
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: '600',
      marginBottom: '0.5rem',
      color: '#374151'
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      fontFamily: 'inherit'
    },
    select: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      fontFamily: 'inherit',
      backgroundColor: 'white'
    },
    formActions: {
      display: 'flex',
      gap: '1rem',
      gridColumn: '1 / -1'
    },
    alert: {
      padding: '1rem',
      borderRadius: '8px',
      marginBottom: '1rem',
      fontSize: '0.875rem',
      fontWeight: '500'
    },
    alertError: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      border: '1px solid #fecaca'
    },
    alertSuccess: {
      backgroundColor: '#dcfce7',
      color: '#166534',
      border: '1px solid #bbf7d0'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      backgroundColor: 'white',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    th: {
      backgroundColor: '#f3f4f6',
      padding: '1rem',
      textAlign: 'left',
      fontSize: '0.875rem',
      fontWeight: '600',
      color: '#374151',
      borderBottom: '1px solid #e5e7eb'
    },
    td: {
      padding: '1rem',
      borderBottom: '1px solid #e5e7eb',
      fontSize: '0.875rem'
    },
    statusBadge: {
      display: 'inline-block',
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: '600',
      textTransform: 'capitalize'
    },
    statusActive: {
      backgroundColor: '#dcfce7',
      color: '#166534'
    },
    statusLeave: {
      backgroundColor: '#fee2e2',
      color: '#991b1b'
    },
    statusBackup: {
      backgroundColor: '#fef3c7',
      color: '#92400e'
    },
    actions: {
      display: 'flex',
      gap: '0.5rem'
    },
    iconButton: {
      padding: '0.5rem',
      backgroundColor: '#f3f4f6',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    editButton: {
      color: '#0891b2'
    },
    deleteButton: {
      color: '#dc2626'
    }
  };

  const getStatusBadgeStyle = (status) => {
    const baseStyle = { ...styles.statusBadge };
    if (status === 'active') return { ...baseStyle, ...styles.statusActive };
    if (status === 'leave') return { ...baseStyle, ...styles.statusLeave };
    if (status === 'backup') return { ...baseStyle, ...styles.statusBackup };
    return baseStyle;
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Driver Management</h1>
        <div style={{ display: 'flex', gap: '1rem' }}>
          {showForm && (
            <button style={{ ...styles.button, ...styles.buttonSecondary }} onClick={handleCancel}>
              <X size={18} />
              Cancel
            </button>
          )}
          {!showForm && (
            <button style={styles.button} onClick={() => setShowForm(true)}>
              <Plus size={18} />
              Add Driver
            </button>
          )}
          <button style={{ ...styles.button, ...styles.buttonSecondary }} onClick={onBack}>
            Back
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div style={{ ...styles.alert, ...styles.alertError }}>
          {error}
        </div>
      )}

      {/* Success Alert */}
      {success && (
        <div style={{ ...styles.alert, ...styles.alertSuccess }}>
          {success}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <form style={styles.form} onSubmit={handleSubmit}>
          <h2 style={{ marginBottom: '1.5rem', fontSize: '1.25rem', fontWeight: '600' }}>
            {editingDriver ? 'Edit Driver' : 'Add New Driver'}
          </h2>

          <div style={styles.formGroup}>
            <div>
              <label style={styles.label}>Driver Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., Ali A"
                style={styles.input}
              />
            </div>
            <div>
              <label style={styles.label}>Phone Number *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="e.g., (206) 474-7089"
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <div>
              <label style={styles.label}>Access Code (4 digits) *</label>
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleInputChange}
                placeholder="e.g., 9801"
                maxLength="4"
                style={styles.input}
              />
            </div>
            <div>
              <label style={styles.label}>Status *</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                style={styles.select}
              >
                <option value="active">Active</option>
                <option value="leave">On Leave</option>
                <option value="backup">Backup Only</option>
              </select>
            </div>
          </div>

          <div style={styles.formGroup}>
            <div>
              <label style={styles.label}>Max Capacity (students per day)</label>
              <input
                type="number"
                name="maxCapacity"
                value={formData.maxCapacity}
                onChange={handleInputChange}
                min="1"
                max="50"
                style={styles.input}
              />
            </div>
          </div>

          {/* Home Address + Drive Time to Stemtree */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={styles.label}>Home Address</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="e.g. 123 Main St, Redmond, WA"
                style={styles.input}
              />
            </div>
            <div>
              <label style={styles.label}>Drive Time: Home → Stemtree (min)</label>
              <input
                type="number"
                min="0"
                value={formData.homeToStemtree || ''}
                onChange={e => setFormData(prev => ({ ...prev, homeToStemtree: parseInt(e.target.value) || 0 }))}
                placeholder="e.g. 12"
                style={styles.input}
              />
              <p style={{ fontSize: '0.72rem', color: '#6b7280', margin: '0.2rem 0 0' }}>
                Look up on Google Maps once. Used to calculate departure time.
              </p>
            </div>
          </div>

          {/* Drive Times per School (Stemtree → School only) */}
          {pickupSchools.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={styles.label}>Drive Times: Stemtree → Each School (minutes)</label>
              <p style={{ fontSize: '0.72rem', color: '#6b7280', margin: '0.25rem 0 0.75rem' }}>
                Enter one-time drive times from Stemtree to each school. Used to calculate when driver must leave center.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {pickupSchools.map(school => {
                  const dt = formData.driveTimes?.[school.name] || {};
                  return (
                    <div key={school.name} style={{ display: 'flex', alignItems: 'center', gap: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px', padding: '0.6rem 1rem', border: '1px solid #e5e7eb' }}>
                      <div style={{ flex: 1, fontWeight: '600', fontSize: '0.82rem', color: '#1f2937' }}>{school.name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                        <label style={{ fontSize: '0.75rem', color: '#6b7280', whiteSpace: 'nowrap' }}>From Stemtree (min)</label>
                        <input
                          type="number"
                          min="0"
                          value={dt.fromStemtree || ''}
                          onChange={e => setFormData(prev => ({
                            ...prev,
                            driveTimes: {
                              ...prev.driveTimes,
                              [school.name]: { ...dt, fromStemtree: parseInt(e.target.value) || 0 }
                            }
                          }))}
                          placeholder="e.g. 18"
                          style={{ ...styles.input, width: '80px', padding: '0.4rem 0.5rem' }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={styles.formActions}>
            <button type="submit" style={styles.button}>
              {editingDriver ? 'Update Driver' : 'Add Driver'}
            </button>
            <button
              type="button"
              style={{ ...styles.button, ...styles.buttonSecondary }}
              onClick={handleCancel}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Loading State */}
      {loading && <div style={{ textAlign: 'center', padding: '2rem' }}>Loading drivers...</div>}

      {/* Drivers Table */}
      {!loading && drivers.length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Phone</th>
              <th style={styles.th}>Access Code</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Max Capacity</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {drivers.map(driver => (
              <tr key={driver.id}>
                <td style={styles.td}>{driver.name}</td>
                <td style={styles.td}>{driver.phone}</td>
                <td style={styles.td}>
                  <code style={{ backgroundColor: '#f3f4f6', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>
                    {driver.code}
                  </code>
                </td>
                <td style={styles.td}>
                  <span style={getStatusBadgeStyle(driver.status)}>
                    {driver.status}
                  </span>
                </td>
                <td style={styles.td}>{driver.maxCapacity || 15}</td>
                <td style={styles.td}>
                  <div style={styles.actions}>
                    <button
                      style={{ ...styles.iconButton, ...styles.editButton }}
                      onClick={() => handleEdit(driver)}
                      title="Edit driver"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      style={{ ...styles.iconButton, ...styles.deleteButton }}
                      onClick={() => handleDelete(driver.id)}
                      title="Delete driver"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Empty State */}
      {!loading && drivers.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          backgroundColor: '#f8fafc',
          borderRadius: '12px',
          border: '1px dashed #d1d5db'
        }}>
          <p style={{ color: '#6b7280', marginBottom: '1rem' }}>No drivers yet</p>
          <button style={styles.button} onClick={() => setShowForm(true)}>
            <Plus size={18} />
            Add Your First Driver
          </button>
        </div>
      )}
    </div>
  );
};

export default DriverManagement;